import { useEffect, useMemo, useState } from 'react';
import useFetchList from 'hooks/useFetchList';
import { useDispatch, useSelector } from 'react-redux';
import { GET_ALERT_COLUMNS } from '../constants';
import { NULL_QUERY_DELAY } from '../../Shared/constants';
import {
  getNewAlertsNotification,
  sendViewedAlertsUuids,
  REQUEST_STATUS,
  setAlertsPreFilter,
  addViewedState,
  TABLE_KEYS,
  navigateToRoute,
} from '../../../redux_store';
import { checkPermission } from '../../../helpers/checkPermissions';
import permissions from '../../../config/permissions';
import { camelCase } from 'lodash';
import { ASSESSMENT_STATUSES } from '../../Assessments/constants';
import { ALL_ROUTES } from '../../../config/routes';
import QhsTable from '../../Shared/Table/Table';
import { convertDataFromApi, useGenerateColumns } from '../../Shared/Table/helpers';
import TableActions from './widgets/TableActions';
import RowActions from './widgets/RowActions';
import MetricsButton from '../../Shared/Table/widgets/MetricsButton';
import TablePageContainer from '../../Shared/Table/widgets/TablePageContainer';
import TableTitle from '../../Shared/Table/widgets/TableTitle';

let timeoutID;

const AlertList = () => {
  const {
    metadata: { currentAccount, currentAccountPermissions },
    isDistributionSponsor,
  } = useSelector(state => state.user);
  const dispatch = useDispatch();
  const {
    en: { titles },
  } = useSelector(state => state.i18n);
  const { tablePreFilters, statusSendingViewedAlertsUuids } = useSelector(state => state.alerts);
  const [modifiedAlerts, setModifiedAlerts] = useState();
  const canManageAlerts = checkPermission(currentAccountPermissions, permissions.ALERT_ACTION);
  const tableKey = isDistributionSponsor ? TABLE_KEYS.alertsDSTable : TABLE_KEYS.alertsTable;
  const { viewState = {} } = useSelector(state => state.table);
  const { newViewed = [] } = viewState;
  const fields = GET_ALERT_COLUMNS.map(columnData => columnData.accessorKey)
    .sort()
    .join();

  const {
    items,
    totalCount,
    filter,
    sort,
    onSort,
    onFilter,
    onLoadMoreRows,
    reloadData,
    clearFilter,
    onMultiColumnFilter,
    aggregations,
    loading: dataLoading,
    additionalLoadsResults,
    fetchError,
  } = useFetchList({
    baseEndpoint: `/accounts/${currentAccount.uuid}/alerts`,
    baseParams: {
      fields: fields,
    },
    additionalLoads: {
      alertTags: '/accounts/alerts/tags',
    },
    baseFilter: tablePreFilters,
    tableKey,
    canLoad: fields.length,
  });

  const columns = useGenerateColumns(
    GET_ALERT_COLUMNS,
    tableKey,
    {},
    additionalLoadsResults
  ).columns;

  const sendToBackendViewedAlertsUuids = () => {
    if (newViewed.length) {
      dispatch(
        sendViewedAlertsUuids({
          accountUuid: currentAccount.uuid,
          viewedAlertsUuids: newViewed,
        })
      );
      dispatch(
        addViewedState({
          newViewed: [],
        })
      );
    }
  };

  useEffect(() => {
    if (statusSendingViewedAlertsUuids === REQUEST_STATUS.succeeded) {
      setTimeout(() => {
        dispatch(getNewAlertsNotification(currentAccount.uuid));
      }, 500);
    }
  }, [statusSendingViewedAlertsUuids]);

  useEffect(() => {
    if (newViewed.length) {
      clearTimeout(timeoutID);
      timeoutID = setTimeout(() => {
        sendToBackendViewedAlertsUuids();
      }, 2000);
    }
  }, [newViewed]);

  useEffect(() => {
    if (fields && filter !== null && JSON.stringify(filter) !== JSON.stringify(tablePreFilters)) {
      onMultiColumnFilter(tablePreFilters);
    }
  }, [tablePreFilters, fields]);

  useEffect(() => {
    if (items) {
      let newNotViewedUuids = [];
      items.forEach(alert => !alert.viewedBy && newNotViewedUuids.push(alert.uuid));
      dispatch(
        addViewedState({
          notViewedUuids: newNotViewedUuids,
        })
      );

      const newModifiedItems = convertDataFromApi(
        items,
        tableKey,
        filter,
        {
          errorType: alert => alert.alertType === 'critical',
        },
        additionalLoadsResults
      );

      setModifiedAlerts(newModifiedItems);
    }
  }, [items]);

  const reloadAlertList = hasFilter => {
    if (!hasFilter) {
      clearFilter();
    } else {
      reloadData({ delay: NULL_QUERY_DELAY });
    }
  };

  const redirectToAssessmentReport = ({ accountUUID, entityUUID, patientUUID, entityName }) => {
    const isItChildAccount = entityUUID !== currentAccount.uuid && isDistributionSponsor;
    dispatch(
      navigateToRoute({
        route: {
          path: (isItChildAccount ? ALL_ROUTES.assessment.reportDS : ALL_ROUTES.assessment.report)
            .path,
          params: {
            currentAccountUuid: currentAccount.uuid,
            accountUuid: isItChildAccount ? accountUUID : null,
            patientUuid: patientUUID,
            assessmentUuid: entityUUID,
            status: camelCase(ASSESSMENT_STATUSES.completed),
          },
          title: `${entityName} ${titles.report}`,
        },
      })
    );
  };

  const _onFilter = (_filter, delay) => {
    onFilter(_filter, delay);
    dispatch(setAlertsPreFilter(_filter(filter)));
  };

  const hasFilters = useMemo(
    () =>
      !!filter?.reduce((accumulator, { value }) => {
        return accumulator + value.toString().replaceAll(' ', '').length;
      }, 0),
    [filter]
  );

  return (
    <TablePageContainer>
      <QhsTable
        columns={columns}
        data={modifiedAlerts || []}
        fetchNextPage={onLoadMoreRows}
        isFetching={dataLoading}
        isLoading={dataLoading && !modifiedAlerts}
        totalRowCount={totalCount}
        sorting={sort}
        setSorting={onSort}
        filter={filter}
        setFilters={_onFilter}
        tableKey={tableKey}
        onRowClick={redirectToAssessmentReport}
        showRefreshTableButton
        fetchError={fetchError}
        state={{
          canManageViewed: canManageAlerts,
          hasManageViewed: true,
        }}
        reloadData={reloadAlertList}
        tableTitle={
          <TableTitle
            titleText={titles.alerts}
            count={aggregations?.PatientsCount || 0}
            countFiltered={hasFilters}
          />
        }
        menuActions={
          <MetricsButton
            title={'alerts'}
            aggregations={dataLoading && !modifiedAlerts ? null : aggregations}
            filter={filter}
          />
        }
        tableMenuPopoverOptions={
          canManageAlerts
            ? setAnchorElMulti => (
                <TableActions
                  totalCount={totalCount}
                  reloadData={reloadData}
                  setAnchorElMulti={setAnchorElMulti}
                  sort={sort}
                  filter={filter}
                />
              )
            : null
        }
        renderRowActionMenuItems={({ row, setAnchorEl }) => (
          <RowActions row={row} setAnchorEl={setAnchorEl} reloadData={reloadData} />
        )}
      />
    </TablePageContainer>
  );
};

export default AlertList;
